import { ActionFunctionArgs } from "@remix-run/node"
import { Form, Link, useActionData, useNavigation } from "@remix-run/react"
import { z } from "zod"
import { Button } from "~/components/ui/button"
import { Input } from "~/components/ui/input"
import { Label } from "~/components/ui/label"
import { FormValidationMessage, ValidationMessage } from "~/components/ui/validation-message"
import { login } from "~/lib/auth"
import { getFieldErrors, parseIssues } from "~/lib/utils"

const loginSchema = z.object({
    email: z.string().min(1, {
        message: "This field is required.",
    }),
    password: z.string().min(1, {
        message: "This field is required.",
    }),
})

export async function action({ request }: ActionFunctionArgs) {
    const formData = await request.formData()
    const validation = loginSchema.safeParse(Object.fromEntries(formData.entries()))

    if (!validation.success) {
        return {
            status: "error",
            type: "validation",
            message: "Form did not pass validation.",
            errors: parseIssues(validation.error.issues),
        }
    }

    // will redirect if a valid login
    const result = await login({ email: validation.data.email, password: validation.data.password })

    return {
        status: result.status,
        type: "server",
        message: result.message,
        errors: [],
    }
}

export default function Login() {
    const actionData = useActionData<typeof action>()
    const navigation = useNavigation()

    return (
        <div>
            <div className="text-center mb-6">
                <h1 className="text-2xl font-bold mb-1">Welcome back</h1>
                <p className="text-sm font-medium text-gray-400">Login to your account below</p>
            </div>
            <Form method="post" className="space-y-4 mb-6">
                {actionData && actionData.status == "error" && actionData.type == "server" && (
                    <FormValidationMessage variant="error" message={actionData.message} />
                )}
                <div className="space-y-2">
                    <Label htmlFor="email" isRequired>
                        Email
                    </Label>
                    <Input name="email" />
                    <ValidationMessage message={getFieldErrors(actionData?.errors, "email")} />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="password" isRequired>
                        Password
                    </Label>
                    <Input name="password" type="password" />
                    <ValidationMessage message={getFieldErrors(actionData?.errors, "password")} />
                    <span className="text-[0.8rem] font-medium text-gray-400">
                        <Link to={"/reset-password"}>Forgot your password?</Link>
                    </span>
                </div>
                <div>
                    <Button type="submit" className="mt-2 w-full" isLoading={navigation.state === "submitting"}>
                        Signin
                    </Button>
                </div>
            </Form>
            <p className="text-[0.8rem] font-medium text-primary text-center">
                <Link to={"/signup"}>Don't have an account?</Link>
            </p>
        </div>
    )
}
